/**
 * FleetSummaryWidget — Dashboard-Kachel mit
 * (1) offenen Schäden / Reparaturen
 * (2) bald ablaufenden Fahrzeug-Terminen (TÜV/HU, Inspektion, Vertragsende, Reifen)
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Car, Wrench, AlertTriangle, ArrowRight, Calendar } from 'lucide-react';
import { vehiclesAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

const EVENT_LABEL = {
  damage: 'Schaden',
  maintenance: 'Wartung',
  repair: 'Reparatur',
  other: 'Sonstiges',
};

const STATUS_LABEL = {
  open: 'Offen',
  in_progress: 'In Bearbeitung',
};

const formatDeadline = (item) => {
  const dt = item.due_date
    ? new Date(item.due_date).toLocaleDateString('de-DE')
    : '—';
  if (item.is_overdue) return `Überfällig seit ${Math.abs(item.days_until)} T. (${dt})`;
  if (item.days_until === 0) return `Heute fällig (${dt})`;
  return `In ${item.days_until} T. (${dt})`;
};

const deadlineBadge = (item) => {
  if (item.is_overdue) return 'bg-red-100 text-red-800';
  if (item.days_until <= 7) return 'bg-amber-100 text-amber-800';
  return 'bg-blue-100 text-blue-800';
};

export const FleetSummaryWidget = () => {
  const { hasPermission, isAdmin } = useAuth();
  const canViewFleet = isAdmin || hasPermission('fleet', 'view');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(canViewFleet);

  const load = useCallback(async () => {
    if (!canViewFleet) {
      setLoading(false);
      return;
    }
    try {
      const r = await vehiclesAPI.dashboardSummary();
      setData(r.data);
    } catch (e) {
      // Keine Fleet-Permission → Widget ausblenden
      if (e.response?.status === 403) setData(null);
    } finally {
      setLoading(false);
    }
  }, [canViewFleet]);

  useEffect(() => { load(); }, [load]);

  if (!canViewFleet) return null;
  if (loading) {
    return (
      <Card className="border-border/60">
        <CardHeader className="border-b border-border/40 pb-4">
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent className="p-4 space-y-3">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;
  if (data.open_events_total === 0 && data.deadlines_total === 0) return null;

  return (
    <Card className="border-border/60" data-testid="fleet-summary-widget">
      <CardHeader className="border-b border-border/40 pb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <CardTitle className="font-heading text-lg flex flex-wrap items-center gap-2">
          <Car className="h-5 w-5 text-primary shrink-0" />
          Fuhrpark
          {data.open_events_total > 0 && (
            <Badge variant="destructive">{data.open_events_total} offen</Badge>
          )}
        </CardTitle>
        <Link to="/fuhrpark" className="shrink-0 self-start sm:self-auto">
          <Button variant="ghost" size="sm" className="text-xs whitespace-nowrap" data-testid="fleet-widget-view-all">
            Zum Fuhrpark <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </Link>
      </CardHeader>
      <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <section>
          <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1">
            <Wrench className="h-3.5 w-3.5" /> Offene Schäden / Wartungen
          </h3>
          {data.open_events.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">Keine offenen Einträge</p>
          ) : (
            <ul className="space-y-1.5" data-testid="fleet-open-events">
              {data.open_events.map((ev) => (
                <li key={ev.id} className="flex items-center justify-between gap-2 text-sm border-b border-border/40 pb-1.5">
                  <div className="min-w-0">
                    <p className="font-medium truncate">{ev.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {ev.license_plate} · {EVENT_LABEL[ev.event_type] || ev.event_type} · {STATUS_LABEL[ev.status] || ev.status}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {data.open_events_total > data.open_events.length && (
            <p className="text-xs text-muted-foreground mt-2">
              + {data.open_events_total - data.open_events.length} weitere
            </p>
          )}
        </section>

        <section>
          <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" /> Bald fällige Termine (≤ 60 Tage)
          </h3>
          {data.deadlines.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">Keine anstehenden Termine</p>
          ) : (
            <ul className="space-y-1.5" data-testid="fleet-deadlines">
              {data.deadlines.map((dl) => (
                <li key={`${dl.vehicle_id}-${dl.type}`} className="flex items-center justify-between gap-2 text-sm border-b border-border/40 pb-1.5">
                  <div className="min-w-0">
                    <p className="font-medium truncate flex items-center gap-1">
                      {dl.is_overdue && <AlertTriangle className="h-3.5 w-3.5 text-red-600 shrink-0" />}
                      {dl.license_plate} · {dl.type}
                    </p>
                  </div>
                  <Badge className={`shrink-0 ${deadlineBadge(dl)}`}>{formatDeadline(dl)}</Badge>
                </li>
              ))}
            </ul>
          )}
          {data.deadlines_total > data.deadlines.length && (
            <p className="text-xs text-muted-foreground mt-2">
              + {data.deadlines_total - data.deadlines.length} weitere
            </p>
          )}
        </section>
      </CardContent>
    </Card>
  );
};
